import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const SUPABASE_URL = 'https://lsbmvfjymydutatevugb.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxzYm12Zmp5bXlkdXRhdGV2dWdiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMwNTY3ODAsImV4cCI6MjA1ODYzMjc4MH0.wE7EET3jD-z4uwLpo-YBfeY1EsUljX5R_gW2n4Beyo0';

interface CreateTemplateData {
  teacher_code: string;
  weekday: number;
  start_time: string;
  end_time: string;
  course_type: 'REGULAR' | 'INTER';
  class_type: 'PREMIER' | 'PRIVATE';
  education_level: string;
  language: 'THAI' | 'ENGLISH';
  subject: string;
  location: string;
  room_name?: string;
  is_special_class?: boolean;
  fixed_cost?: number;
  note?: string;
  status?: 'NORMAL' | 'PAUSED';
  student_codes?: string[];
}

interface UpdateTemplateData extends Partial<CreateTemplateData> {
  id: string;
}

async function getAuthHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  
  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
    'Content-Profile': 'fishsix',
    'Accept-Profile': 'fishsix',
    'Prefer': 'return=representation',
  };
}

async function insertAdminLog(log: {
  template_id?: string | null;
  teacher_code: string;
  action: string;
  changes: Record<string, unknown>;
}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;

  const headers = {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };

  await fetch(`${SUPABASE_URL}/rest/v1/class_template_admin_logs`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      template_id: log.template_id || null,
      teacher_code: log.teacher_code,
      action: log.action,
      changes: log.changes,
      admin_id: session.user.id,
      admin_email: session.user.email || '',
    }),
  });
}

export function useClassTemplateMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createTemplate = useMutation({
    mutationFn: async (data: CreateTemplateData) => {
      const { student_codes, ...templateData } = data;
      const headers = await getAuthHeaders();

      const createResponse = await fetch(`${SUPABASE_URL}/rest/v1/class_templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify(templateData),
      });

      if (!createResponse.ok) {
        const error = await createResponse.json();
        throw new Error(error.message || 'Failed to create template');
      }

      const [template] = await createResponse.json();

      if (student_codes && student_codes.length > 0) {
        const studentInserts = student_codes.map((code) => ({
          template_id: template.id,
          student_code: code,
        }));

        const studentsResponse = await fetch(`${SUPABASE_URL}/rest/v1/class_template_students`, {
          method: 'POST',
          headers,
          body: JSON.stringify(studentInserts),
        });

        if (!studentsResponse.ok) {
          const error = await studentsResponse.json();
          throw new Error(error.message || 'Failed to add students');
        }
      }

      // Log admin action
      await insertAdminLog({
        template_id: template.id,
        teacher_code: data.teacher_code,
        action: 'CREATE',
        changes: { ...templateData, student_codes },
      });

      return template;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class-templates'] });
      toast({
        title: 'สำเร็จ',
        description: 'เพิ่มตารางเรียนเรียบร้อยแล้ว',
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async (data: UpdateTemplateData) => {
      const { id, student_codes, ...templateData } = data;
      const headers = await getAuthHeaders();

      const updateResponse = await fetch(`${SUPABASE_URL}/rest/v1/class_templates?id=eq.${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(templateData),
      });

      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        throw new Error(error.message || 'Failed to update template');
      }

      if (student_codes !== undefined) {
        const deleteResponse = await fetch(
          `${SUPABASE_URL}/rest/v1/class_template_students?template_id=eq.${id}`,
          {
            method: 'DELETE',
            headers,
          }
        );

        if (!deleteResponse.ok) {
          const error = await deleteResponse.json();
          throw new Error(error.message || 'Failed to delete students');
        }

        if (student_codes.length > 0) {
          const studentInserts = student_codes.map((code) => ({
            template_id: id,
            student_code: code,
          }));

          const studentsResponse = await fetch(`${SUPABASE_URL}/rest/v1/class_template_students`, {
            method: 'POST',
            headers,
            body: JSON.stringify(studentInserts),
          });

          if (!studentsResponse.ok) {
            const error = await studentsResponse.json();
            throw new Error(error.message || 'Failed to add students');
          }
        }
      }

      // Log admin action
      await insertAdminLog({
        template_id: id,
        teacher_code: data.teacher_code || '',
        action: 'UPDATE',
        changes: { updated_fields: templateData, student_codes },
      });

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class-templates'] });
      toast({
        title: 'สำเร็จ',
        description: 'แก้ไขตารางเรียนเรียบร้อยแล้ว',
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async ({ id, teacher_code }: { id: string; teacher_code: string }) => {
      const headers = await getAuthHeaders();

      const response = await fetch(`${SUPABASE_URL}/rest/v1/class_templates?id=eq.${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete template');
      }

      // Log admin action
      await insertAdminLog({
        template_id: id,
        teacher_code,
        action: 'DELETE',
        changes: { deleted_template_id: id },
      });

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class-templates'] });
      toast({
        title: 'สำเร็จ',
        description: 'ลบตารางเรียนเรียบร้อยแล้ว',
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active, teacher_code, status }: { id: string; is_active: boolean; teacher_code: string; status?: 'NORMAL' | 'PAUSED' }) => {
      const headers = await getAuthHeaders();

      const payload: { is_active: boolean; status?: 'NORMAL' | 'PAUSED' } = { is_active };
      if (status) {
        payload.status = status;
      }

      const response = await fetch(`${SUPABASE_URL}/rest/v1/class_templates?id=eq.${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to toggle active status');
      }

      // Log admin action
      await insertAdminLog({
        template_id: id,
        teacher_code,
        action: is_active ? 'TOGGLE_ACTIVE' : 'TOGGLE_DEACTIVE',
        changes: { is_active, ...(status ? { status } : {}) },
      });

      return { id, is_active, status };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['class-templates'] });
      toast({
        title: 'สำเร็จ',
        description: data.is_active
          ? data.status === 'NORMAL'
            ? 'เปิดใช้งานตารางเรียน และปรับเป็นสอนปกติแล้ว'
            : 'เปิดใช้งานตารางเรียน'
          : 'ปิดใช้งานตารางเรียน',
      });
    },
    onError: (error) => {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    toggleActive,
  };
}
